import { useAppDispatch, useAppSelector } from '@common/hooks'
import React, { useRef, useState, useEffect, useCallback } from 'react'
import { useStreamRecordingUnsw } from './use-stream'
import {
  useRevisionCancelRecordingMutation,
  useRevisionCreateChunkRecordingSettingsMutation,
} from '@app/services/auto-generated/revision'
import { setRecordingStatus } from '@features/recordings/recordings.slice'
import { MediaRecorder as ExtendableMediaRecorder, register } from 'extendable-media-recorder'
import { useStopwatch } from 'react-timer-hook'
import { connect } from 'extendable-media-recorder-wav-encoder'
import { v4 as uuidv4 } from 'uuid'
import { refreshTokenIfExpired } from '@features/auth/utils/refresh-token-if-expired'
import { store } from '@app/store'
import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile } from '@ffmpeg/util'
import coreURL from '@ffmpeg/core?url'
import wasmURL from '@ffmpeg/core/wasm?url'
import { useCurrentEvent } from '@features/event/hooks'

const useRecording = (
  testMyself: boolean,
  selectedDevice?: string,
  liveTranscribe: boolean = true,
) => {
  const { seconds, minutes, start, pause, reset } = useStopwatch({
    autoStart: false,
  })
  const dispatch = useAppDispatch()
  const [analyserNode, setAnalyserNode] = React.useState(null)
  const [audioData, setAudioData] = React.useState(new Uint8Array(0))
  const { recordingStatus } = useAppSelector((state) => state.recordings)
  const { isSuccess, uploadRecording, stopUploadRecording } = useStreamRecordingUnsw(testMyself)
  const [submitSettings] = useRevisionCreateChunkRecordingSettingsMutation()
  const [deleteRecording] = useRevisionCancelRecordingMutation()
  const [processedChunks, setProcessedChunks] = React.useState<string[]>([])
  const [ready, setReady] = React.useState(false)
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null)
  const [totalprocessedChunks, setTotalprocessedChunks] = React.useState<number>(0)
  const [recordingUUID, setRecordingUUID] = React.useState<string>()
  const CHUNK_SIZE_IN_SECONDS = 5
  const mediaStreamRef = React.useRef<MediaStream | null>(null)
  const [isCanceled, setIsCanceled] = React.useState(false)
  const { event } = useCurrentEvent()
  const cleanUp = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    mediaRecorderRef.current?.stop()
    mediaRecorderRef.current = null
    mediaStreamRef.current = null
  }

  const chunks = React.useRef<Blob[]>([])

  const WEBSOCKET_CHUNK_INTERVAL = 5000
  const [socket, setSocket] = React.useState<WebSocket | null>(null)
  const ffmpegRef = useRef(new FFmpeg())
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false)

  const [loadingProgress, setLoadingProgress] = useState(0)

  useEffect(() => {
    const loadFFmpeg = async () => {
      const ffmpeg = ffmpegRef.current

      try {
        // Fetch core file
        const coreResponse = await fetch(coreURL)
        const coreBlob = await coreResponse.blob()
        const coreBlobURL = URL.createObjectURL(coreBlob)

        // Fetch and track WASM file progress
        const wasmResponse = await fetch(wasmURL, {
          headers: {
            'Content-Type': 'application/wasm',
          },
        })

        const contentLength = wasmResponse.headers.get('Content-Length')
        const total = parseInt(contentLength ?? '0', 10)

        const reader = wasmResponse.body?.getReader()
        const chunks: Uint8Array[] = []
        let receivedLength = 0

        while (true) {
          const { done, value } = await reader!.read()
          if (done) break

          chunks.push(value)
          receivedLength += value.length
          setLoadingProgress(Math.round((receivedLength / total) * 100))
        }

        // Combine chunks into a single Uint8Array
        const wasmData = new Uint8Array(receivedLength)
        let position = 0
        for (const chunk of chunks) {
          wasmData.set(chunk, position)
          position += chunk.length
        }

        // Create blob URL for WASM
        const wasmBlob = new Blob([wasmData.buffer], { type: 'application/wasm' })
        const wasmBlobURL = URL.createObjectURL(wasmBlob)

        // Load FFmpeg with blob URLs
        await ffmpeg.load({
          coreURL: coreBlobURL,
          wasmURL: wasmBlobURL,
        })

        // Clean up blob URLs
        URL.revokeObjectURL(coreBlobURL)
        URL.revokeObjectURL(wasmBlobURL)

        console.log('FFmpeg loaded')
        setFfmpegLoaded(true)
      } catch (error) {
        console.error('Error loading FFmpeg:', error)
      }
    }

    loadFFmpeg()
  }, [])

  const connectWebSocket = async (recordingId: string) => {
    try {
      await refreshTokenIfExpired()
      const tokens = store.getState().auth.tokens
      const ws = new WebSocket(
        `wss://proxy.ispeak.to/ws?token=${tokens?.access_token?.token}&recording_id=${recordingId}`,
      )

      ws.onopen = () => {
        // this is for openai i'll leave it here for now
        const sessionUpdateMessage = {
          type: 'session.update',
          session: {
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 200,
              silence_duration_ms: 300,
            },
            voice: 'alloy',
            instructions: 'you are a helpful assistant.',
            temperature: 0.7,
            max_response_output_tokens: 800,
          },
        }
        ws.send(JSON.stringify(sessionUpdateMessage))
      }

      ws.onclose = () => {
        console.log('WebSocket disconnected')
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      return ws
    } catch (error) {
      console.error('Error connecting to WebSocket:', error)
      return null
    }
  }

  const startRecording = async () => {
    if (recordingStatus) {
      return null
    } else {
      setIsCanceled(false)
      try {
        const constraints = selectedDevice
          ? {
              audio: {
                deviceId: { exact: selectedDevice },
              },
            }
          : { audio: true }

        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        dispatch(setRecordingStatus(true))
        mediaStreamRef.current = stream

        // Create AudioContext and AnalyserNode
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 2048

        // Connect the stream to the analyser
        const source = audioContext.createMediaStreamSource(stream)
        source.connect(analyser)

        setAnalyserNode(analyser)

        const settings = stream.getAudioTracks()[0].getSettings()
        const recordingId = uuidv4()
        if (!event) {
          await submitSettings({
            recordingId: recordingId,
            createRecordingChunkSettingsRequest: {
              sample_rate: settings.sampleRate,
              channels: settings.channelCount,
              chunk_size: CHUNK_SIZE_IN_SECONDS,
              sample_width: settings.sampleSize,
            },
          })
        } else {
          await submitSettings({
            recordingId: recordingId,
            createRecordingChunkSettingsRequest: {
              sample_rate: settings.sampleRate,
              channels: settings.channelCount,
              chunk_size: CHUNK_SIZE_IN_SECONDS,
              sample_width: settings.sampleSize,
              event_id: event?.event_id,
            },
          })
        }
        setRecordingUUID(recordingId)
        const mediaRecorder = new ExtendableMediaRecorder(stream, { mimeType: 'audio/wav' })
        mediaRecorderRef.current = mediaRecorder

        let ws: WebSocket | null = null
        if (liveTranscribe) {
          ws = await connectWebSocket(recordingId)
          setSocket(ws)
        }

        let accumulatedChunks: Blob[] = []
        let lastUploadTime = Date.now()
        let isUploading = false

        mediaRecorder.ondataavailable = async (e) => {
          if (mediaRecorderRef.current?.state !== 'recording') {
            return
          }
          if (e.data && e.data.size > 0) {
            // Handle data for WebSocket (every 100ms)
            if (ws && ws.readyState === WebSocket.OPEN && ffmpegLoaded && liveTranscribe) {
              try {
                const ffmpeg = ffmpegRef.current

                // Write the blob to a file in the FFmpeg virtual file system
                await ffmpeg.writeFile('input.wav', await fetchFile(e.data))

                // Get audio settings
                const settings = mediaStreamRef.current?.getAudioTracks()[0].getSettings()
                const sampleRate = settings?.sampleRate || 48000
                const channels = settings?.channelCount || 1

                // Run FFmpeg command to convert audio
                await ffmpeg.exec([
                  '-f',
                  's16le',
                  '-ar',
                  sampleRate.toString(),
                  '-ac',
                  channels.toString(),
                  '-i',
                  'input.wav',
                  '-ar',
                  '16000',
                  '-fflags',
                  '+genpts',
                  '-preset',
                  'ultrafast',
                  '-threads',
                  '0',
                  '-max_muxing_queue_size',
                  '9999',
                  'output.wav',
                ])

                // Read the converted file
                const outputData = await ffmpeg.readFile('output.wav')
                const arr = new Uint8Array(outputData)
                // Convert to base64
                const base64Audio = btoa(
                  arr.reduce((data, byte) => data + String.fromCharCode(byte), ''),
                )

                const message = {
                  type: 'input_audio_buffer.append',
                  audio: base64Audio,
                }
                ws.send(JSON.stringify(message))
                await ffmpeg.listDir('/')
                // Clean up files in the virtual file system
                await ffmpeg.deleteFile('input.wav')
                await ffmpeg.deleteFile('output.wav')
              } catch (error) {
                console.error('Error processing audio:', error)
              }
            }

            // Accumulate chunks for API upload
            accumulatedChunks.push(e.data)
            console.log('accumulatedChunks', accumulatedChunks.length)
            // Check if 5 seconds have passed since the last upload and not currently uploading
            if (Date.now() - lastUploadTime >= 5000 && !isUploading) {
              isUploading = true
              const blob = new Blob(accumulatedChunks, { type: 'audio/wav' })
              try {
                await uploadRecording(blob, chunks.current.length, recordingId)
                setProcessedChunks((old) => [...old, `recording${chunks.current.length}`])
                chunks.current.push(blob)
                accumulatedChunks = []
                lastUploadTime = Date.now()
              } catch (err) {
                console.error('error', err)
                await stopRecording()
              } finally {
                isUploading = false
              }
            }
          }
        }

        start()
        mediaRecorder.start(WEBSOCKET_CHUNK_INTERVAL)
      } catch (error) {
        dispatch(setRecordingStatus(false))
        reset(undefined, false)
        console.error('Error starting recording:', error)
      }
    }
  }

  const stopRecording = async () => {
    if (mediaRecorderRef.current && recordingStatus) {
      mediaRecorderRef.current.stop()
      reset(undefined, false)
    }
    setReady(true)
    dispatch(setRecordingStatus(false))
  }

  const pauseRecording = () => {
    dispatch(setRecordingStatus(false))
    mediaRecorderRef.current?.pause()
    pause()
  }
  const resumeRecording = () => {
    dispatch(setRecordingStatus(true))
    mediaRecorderRef.current?.resume()
    start()
  }

  const cancelRecording = async () => {
    reset(undefined, false)
    setIsCanceled(true)
    await stopRecording()
    try {
      await deleteRecording({ recordId: recordingUUID })
    } catch (error) {
      console.log('error', error)
    }
    cleanUp()
    chunks.current = []
    setTotalprocessedChunks(0)
    setProcessedChunks([])
    setReady(false)
  }
  React.useEffect(() => {
    const stopRecording = async () => {
      console.log(
        'stop recording',
        processedChunks.length,
        chunks.current.length,
        ready,
        isCanceled,
      )
      if (!isCanceled) {
        await stopUploadRecording(recordingUUID)
      }
      setTotalprocessedChunks(chunks.current.length)
      setReady(false)
      reset(undefined, false)
    }
    if (processedChunks.length === chunks.current.length && ready) {
      stopRecording()
    }
  }, [ready, processedChunks, recordingUUID])

  React.useEffect(() => {
    const setup = async () => {
      await register(await connect())
    }
    setup()

    return () => {
      if (mediaRecorderRef.current) {
        cleanUp()
      }
    }
  }, [])
  const getAudioData = React.useCallback(() => {
    if (analyserNode) {
      const bufferLength = analyserNode.fftSize
      const dataArray = new Uint8Array(bufferLength)
      analyserNode.getByteTimeDomainData(dataArray)
      setAudioData(dataArray)
    }
  }, [analyserNode])

  return {
    recordingStatus,
    isSuccess,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    seconds,
    minutes,
    recordingId: recordingUUID,
    cleanUp,
    reset,
    getAudioData,
    audioData,
    socket,
    ffmpegLoaded,
    stream: mediaStreamRef.current,
    loadingProgress,
  }
}

export default useRecording
